import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, filter, take, takeUntil } from 'rxjs';
import {
    IconElement,
    ImageElement,
    ProductService,
    FullImageElement
} from '../../../shared/services/catalog.service';
import {
    FileUploaderComponent,
    HcModal,
    HcSort,
    HcToastOptions,
    HcToasterService,
    ModalOptions,
    ModalService,
    PaginationComponent,
    SelectChangeEvent,
    SimpleModalOptions,
    TabChangeEvent
} from '@healthcatalyst/cashmere';
import { ChangeElement, ChangeService } from '../../../shared/services/change.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ComponentCanDeactivate } from '../../can-deactivate/component-can-deactivate';
import { OktaService } from '../../../shared/services/okta.service';
import { StatusElement, StatusService } from '../../../shared/services/status.service';
import { ItemService } from '../../../shared/services/item.service';
import { PartnerElement, PartnerService } from '../../../shared/services/partner.service';
import { AdminChangeLogComponent } from '../../individual-change-log/admin-change-log.component';
import { DocumentService } from '../../../shared/services/document.service';
import { LightboxComponent } from '../../lightbox/lightbox.component';
import { environment } from '../../../../environments/environment';

/** Admin product element editor */
@Component({
    selector: 'pd-admin-partners-editor',
    templateUrl: './admin-partners-editor.component.html',
    styleUrls: ['./admin-partners-editor.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AdminPartnersEditorComponent extends ComponentCanDeactivate implements OnInit, OnDestroy {
    private unsubscribe = new Subject<void>();
    modalRef: HcModal<TemplateRef<any>>;
    selectedTab = 0;
    partner: PartnerElement;
    partnerLoading = true;
    partnerError = false;
    imagesEdited = false;
    partnerID: number;
    itemID: number;
    savedItem = -1;
    revisionDate = '';
    partnerForm = new FormGroup({
        nameControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
        previousNamesControl: new FormArray([new FormControl('', { nonNullable: true })]),
        IDControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        statusControl: new FormControl<number | null>(null, { validators: [Validators.required] }),
        published: new FormControl(false, { nonNullable: true }),
        taglineControl: new FormControl('', { nonNullable: true }),
        problemControl: new FormControl('', { nonNullable: true }),
        approachControl: new FormControl('', { nonNullable: true }),
        benefitsFeaturesControl: new FormControl('', { nonNullable: true }),
        iconSVGControl: new FormControl('', { nonNullable: true }),
        iconPNGControl: new FormControl('', { nonNullable: true }),
        iconBase64Control: new FormControl('', { nonNullable: true }),
        techDescControl: new FormControl('', { nonNullable: true }),
        shortDescription: new FormControl('', { nonNullable: true }),
        subtitle: new FormControl('', { nonNullable: true }),
        solutionArea: new FormControl(1, { nonNullable: true })
    });

    selectedImageFiles = new FormControl();
    imageForm = new FormGroup({});
    imgID = 0;
    images: FullImageElement[] = [];
    imagesToDelete: string[] = [];

    quillConfig = {
        toolbar: [['bold', 'italic', 'underline', 'strike'], [{ list: 'ordered' }, { list: 'bullet' }], ['clean'], ['link']]
    };

    quillStyles = {
        'font-family': "'Noto Sans', 'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif"
    };

    statuses: StatusElement[] = [];

    @ViewChild(HcSort)
    sort: HcSort;

    @ViewChild('fileUploaderControl')
    fileUploaderControl: FileUploaderComponent;
    
    @ViewChild(PaginationComponent)
    paginator: PaginationComponent;

    @ViewChild('changeLog')
    changeLog: AdminChangeLogComponent;

    get loading(): boolean {
        return this.partnerLoading;
    }

    get loadError(): boolean {
        return this.partnerError;
    }

    get partnerInputsDirty(): boolean {
        return this.partnerForm.dirty || this.imageForm.dirty || this.imagesEdited;
    }

    constructor(
        private route: ActivatedRoute,
        private productService: ProductService,
        private changeService: ChangeService,
        private toasterService: HcToasterService,
        private oktaService: OktaService,
        private statusService: StatusService,
        private itemService: ItemService,
        private partnerService: PartnerService,
        private modalService: ModalService,
        private documentService: DocumentService
    ) {
        super();
    }

    ngOnInit(): void {
        this.route.queryParams.pipe(takeUntil(this.unsubscribe)).subscribe(params => {
            // If a partner ID is supplied, we're editing an existing partner
            if (params['id']) {
                this.partnerID = params['id'];
            } else {
                // Otherwise we're creating a new partner so generate a new item ID for the partner
                this.itemService.getNewID().pipe(takeUntil(this.unsubscribe)).subscribe({
                    next: data => {                            
                        this.itemID = data['itemNumber'];
                        this.partnerForm.controls['IDControl'].setValue(data['itemNumber']);
                        this.partnerError = false;
                        this.partnerLoading = false;
                    },
                    error: msg => { 
                        console.log( msg );
                        this.partnerError = true;
                        this.partnerLoading = false;
                    }
                });
            }
        });

        this.statusService.statusListUpdated.pipe(takeUntil(this.unsubscribe)).subscribe(list => {
            if (list) {
                this.statuses = this.sortAlphabetically(list);
                this.partnerID && this.loadPartner();
            } else {
                !this.statusService.loadFailed ? this.statusService.refresh() : this.partnerError = true;
            }
        });
    }

    //alphabetical sort helper function
    sortAlphabetically(list: any[]) {
        return list.sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }

    //check if the Item ID is already in use and show an error on the form field if it is
    checkID( uploadModal?: TemplateRef<any> ): void {
        const currentID = this.partnerForm.controls['IDControl'].value!;

        if ( this.savedItem !== currentID ) {
            this.itemService.checkID(currentID).pipe(takeUntil(this.unsubscribe)).subscribe({
                next: data => {        
                    if ( data['itemExists'] ) {
                        this.partnerForm.controls['IDControl'].setErrors({ valid: false });
                    } else if( uploadModal ) {
                        this.onClickSave(uploadModal);
                    }
                },
                error: msg => { 
                    console.log( msg );
                }
            });
        } else if ( uploadModal ) {
            this.onClickSave(uploadModal);
        }
    }

    loadImages(imageArray: ImageElement[]) {
        let productImages: FullImageElement[] = [];
        imageArray.forEach((img: ImageElement) => {
            const placementVal = img.placement ? img.placement : (img.visibleInSheet ? 'Body' : 'Hidden');
            productImages.push({
                id: this.imgID,
                uploadID: img.id,
                type: img.type,
                caption: img.caption,
                fileName: img.fileName,
                visibleInSheet: img.visibleInSheet || false,
                uploadState: 2,
                placement: placementVal
            });

            this.imageForm.addControl('caption' + this.imgID, new FormControl(img.caption.length > 0 ? img.caption : ''));
            this.imageForm.addControl('placement' + this.imgID, new FormControl(placementVal));
            this.imgID++;
        });
        this.images = productImages;
    }

    checkPlacement( event: SelectChangeEvent, index: number ) {
        const placement = event.value;
        if ( placement !== 'Hidden' ) {
            for (let i = 0; i < this.images.length; i++) {
                if ( this.imageForm.get('placement' + this.images[i].id)!.value === placement && i !== index ) {
                    this.imageForm.get('placement' + this.images[i].id)!.setValue('Hidden');
                }
            }
        }
    }

    loadPartner(): void {
        this.partnerService
            .detail(this.partnerID)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: data => {
                    this.revisionDate = data[0].revisionDate;
                    this.itemID = data[0].item;
                    this.savedItem = data[0].item;

                    //parse previous names and icons strings from database
                    if (data[0].previousNames) {
                        data[0].previousNames = JSON.parse(data[0].previousNames);
                        //create form fields and patch previous names

                        //if the number of controls don't match the number of previous names, set up the controls
                        if (data[0].previousNames.length !== this.partnerForm.controls.previousNamesControl.controls.length || data[0].previousNames.length === 1) {
                            for (let i = 0; i < data[0].previousNames.length; i++) {
                                if (i === 0) {
                                    this.partnerForm.controls.previousNamesControl.controls[i].patchValue(data[0].previousNames[0]);
                                } else {
                                    this.partnerForm.controls.previousNamesControl.push((new FormControl(data[0].previousNames[i], { nonNullable: true })));
                                }
                            }
                        }
                    }

                    if (data[0].images) {
                        const imgData = JSON.parse(data[0].images);
                        this.loadImages(imgData);
                    }

                    if (data[0].icons) {
                        data.icons = JSON.parse(data[0].icons);
                    }

                    this.partnerError = false;
                    this.partnerLoading = false;

                    //patch fields with partner data
                    this.partnerForm.patchValue({
                        nameControl: data[0].name,
                        IDControl: data[0].item,
                        statusControl: data[0].statusID,
                        published: data[0].published,
                        taglineControl: data[0].tagline,
                        problemControl: data[0].problem,
                        approachControl: data[0].approach,
                        benefitsFeaturesControl: data[0].features,
                        iconSVGControl: data[0].icons['svgURL'],
                        iconPNGControl: data[0].icons['pngURL'],
                        iconBase64Control: data[0].icons['base64URL'],
                        techDescControl: data[0].techDescription,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea ? data[0].solutionArea : 1
                    });

                    this.partner = {
                        partnerID: data[0].partnerID,
                        name: data[0].name,
                        item: data[0].item,
                        statusID: data[0].statusID,
                        published: data[0].published,
                        revisionDate: data[0].revisionDate,
                        tagline: data[0].tagline,
                        problem: data[0].problem,
                        approach: data[0].approach,
                        features: data[0].features,
                        images: data[0].images,
                        icons: data[0].icons,
                        techDescription: data[0].techDescription,
                        previousNames: data[0].previousNames,
                        shortDescription: data[0].shortDescription,
                        subtitle: data[0].subtitle,
                        solutionArea: data[0].solutionArea
                    };

                    this.changeLog.loadChanges(this.itemID, "partner");

                    this.partnerForm.markAsPristine();
                },
                error: msg => {
                    this.partnerError = true;
                    this.partnerLoading = false;
                    console.log(msg);
                }
            });
    }

    ////Previous Name Functions////
    addPrevName() {
        this.partnerForm.controls.previousNamesControl.push(new FormControl('', { nonNullable: true }));
        this.partnerForm.controls.previousNamesControl.markAsDirty();
    }

    removePrevName(index: number) {
        this.partnerForm.controls.previousNamesControl.removeAt(index);
        this.partnerForm.controls.previousNamesControl.markAsDirty();
    }
    ////End Previous Name Functions////

    ////Image Functions////
    //add images and corresponding form controls to images table
    addImages() {
        //check that all files are images
        let fileCheckFail = false;
        for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
            if (!this.selectedImageFiles.value[i].type.includes('image/')) {
                fileCheckFail = true;
                break;
            }
        }

        if (fileCheckFail) {
            this.selectedImageFiles.markAsTouched();
            this.selectedImageFiles.setErrors({ invalid: true });
        } else {
            for (let i = 0; i < this.selectedImageFiles.value.length; i++) {
                // add form controls and update the array of files to save
                this.imageForm.addControl('caption' + this.imgID, new FormControl(''));
                this.imageForm.addControl('placement' + this.imgID, new FormControl('Hidden'));
                this.images.push({
                    id: this.imgID,
                    uploadID: '',
                    caption: '',
                    type: this.selectedImageFiles.value[i].type,
                    fileName: this.selectedImageFiles.value[i].name,
                    visibleInSheet: false,
                    data: this.selectedImageFiles.value[i],
                    uploadState: 0,
                    placement: 'Hidden'
                });
                this.imgID++;
            }

            this.imagesEdited = true;

            //clear selected images
            if (this.fileUploaderControl) {
                setTimeout(() => {
                    this.fileUploaderControl.reset();
                });
            }
            this.selectedImageFiles = new FormControl();
        }
    }

    // Open confirm removal modal, if the user confirms then remove the image reference
    confirmRemoveImage(img: FullImageElement) {
        const options: SimpleModalOptions = {
            messageHTML: '<p>Are you sure you want to remove the image: <strong>' + img.fileName + '</strong>?<p>',
            headerText: 'Remove Image',
            confirmButtonText: 'Remove Image',
            icon: ''
        };
        this.modalService
            .confirmDestructive(options)
            .pipe(
                take(1),
                filter(confirmed => confirmed)
            )
            .subscribe(() => {
                this.removeImageRef(img);
            });
    }

    //remove the image from the list of images and add it to the array of images to delete if it was already uploaded
    removeImageRef(img: FullImageElement) {
        let imgIndex = -1;
        for (let i = 0; i < this.images.length; i++) {
            if (img.id === this.images[i].id) {
                imgIndex = i;
                break;
            }
        }
        if (imgIndex > -1) {
            this.images.splice(imgIndex, 1);
            if (img.uploadID) {
                this.imagesToDelete.push(img.uploadID);
            }
            this.imagesEdited = true;
        }
    }

    //upload images, one by one
    uploadNextImage(): void {
        // Check if there are any images left to upload
        let nextIndex = -1;
        for (let i = this.images.length - 1; i >= 0; i--) {
            if (!this.images[i].uploadID && this.images[i].uploadState !== -1 && this.images[i].uploadState !== 2) {
                nextIndex = i;
            }
        }

        if (nextIndex !== -1) {
            if (this.images[nextIndex].data) {
                this.images[nextIndex].uploadState = 1;
                this.documentService
                    .addImage(this.images[nextIndex].data!)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        data => {
                            this.images[nextIndex].uploadState = 2;
                            this.images[nextIndex].uploadID = data.location;
                            this.uploadNextImage();
                        },
                        error => {
                            this.images[nextIndex].uploadState = -1;
                            console.log(error);
                            this.uploadNextImage();
                        }
                    );
            } else {
                this.images[nextIndex].uploadState = -1;
                this.uploadNextImage();
            }
        }
    }

    //delete images from blob storage
    deleteImages(): void {
        if (this.imagesToDelete.length > 0) {
            this.imagesToDelete.forEach((element, index) => {
                this.documentService
                    .removeImage(element)
                    .pipe(takeUntil(this.unsubscribe))
                    .subscribe(
                        () => { },
                        error => {
                            console.log(error);
                        }
                    );
            });
        }
    }
    ////End Image Functions////

    //when a user clicks save, delete images and upload unsaved images OR save the technology
    onClickSave(imgUploadModal: TemplateRef<any>) {
        this.deleteImages();

        if (this.images.length > 0) {
            //make list of images that are not already saved
            let unsavedImages: FullImageElement[] = [];
            this.images.forEach(element => {
                if (element.uploadState != 2) {
                    unsavedImages.push(element);
                }
            });

            //if there are unsaved images, save them
            if (unsavedImages.length > 0) {
                const options: ModalOptions = {
                    size: 'lg',
                    ignoreOverlayClick: true,
                    ignoreEscapeKey: true,
                    data: { images: unsavedImages }
                };

                //open upload progress modal
                this.modalRef = this.modalService.open(imgUploadModal, options);
                this.uploadNextImage();
            } else {
                this.savePartner();
            }
        } else {
            this.savePartner();
        }
    }
    
    //// Check for changes in all partner attributes and log them ////
    logChanges(partner: PartnerElement) {
        let changes: ChangeElement[] = [];
        const date = new Date().toISOString();

        //check if name changed
        if (this.partner.name !== partner.name) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Name',
                previous: this.partner.name,
                update: partner.name,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to previous names////
        //check for changes in previous names values
        const prevNamesObj: string[] = typeof this.partner.previousNames === 'object' ? this.partner.previousNames : JSON.parse(this.partner.previousNames);

        const prevStr = JSON.stringify(prevNamesObj);
        const updateStr = JSON.stringify(partner.previousNames);

        //if there's an previous name change, add it
        if (prevStr !== updateStr) {
            const prevNameChange = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Previous Names',
                previous: prevStr,
                update: updateStr,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(prevNameChange);
        }
        ////End checks for changes to previous names////

        //check if item changed
        if (this.partner.item !== partner.item) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Item ID',
                previous: this.partner.item.toString(),
                update: partner.item.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if status changed
        if (this.partner.statusID !== partner.statusID) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Status',
                previous: this.partner.statusID.toString(),
                update: partner.statusID.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if published status changed
        if (this.partner.published !== partner.published) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Published Status',
                previous: this.partner.published.toString(),
                update: partner.published.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }


        //check if tagline changed
        if (this.partner.tagline !== partner.tagline) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Tagline',
                previous: this.partner.tagline,
                update: partner.tagline,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if problem changed
        if (this.partner.problem !== partner.problem) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Problem',
                previous: this.partner.problem,
                update: partner.problem,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if approach changed
        if (this.partner.approach !== partner.approach) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Approach',
                previous: this.partner.approach,
                update: partner.approach,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        //check if features changed
        if (this.partner.features !== partner.features) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Benefits and Features',
                previous: this.partner.features,
                update: partner.features,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if short description changed
        if (this.partner.shortDescription !== partner.shortDescription) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Short Description',
                previous: this.partner.shortDescription,
                update: partner.shortDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if subtitle changed
        if (this.partner.subtitle !== partner.subtitle) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Subtitle',
                previous: this.partner.subtitle,
                update: partner.subtitle,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        // check if Solution Area changed
        if (this.partner.solutionArea !== partner.solutionArea) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Solution Area',
                previous: this.partner.solutionArea ? this.partner.solutionArea.toString() : 'none',
                update: partner.solutionArea.toString(),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        ////Checks for changes to images////
        //check for changes in image attributes
        let imagesChanged = false;

        const prevImgObj: ImageElement[] = typeof this.partner.images === 'object' ? this.partner.images : JSON.parse(this.partner.images);

        partner.images.forEach(element => {
            for (let i = 0; i < prevImgObj.length; i++) {
                if (element.id === prevImgObj[i].id) {
                    if (element.fileName !== prevImgObj[i].fileName) {
                        imagesChanged = true;
                    }

                    if (element.visibleInSheet !== prevImgObj[i].visibleInSheet) {
                        imagesChanged = true;
                    }

                    if (element.caption !== prevImgObj[i].caption) {
                        imagesChanged = true;
                    }

                    if (element.type !== prevImgObj[i].type) {
                        imagesChanged = true;
                    }

                    if (element.placement !== prevImgObj[i].placement) {
                        imagesChanged = true;
                    }
                }
            }
        });

        // check for change in amount of images
        if (partner.images.length !== prevImgObj.length) {
            imagesChanged = true;
        }

        //if there's an image change, add it
        if (imagesChanged) {
            const imageChange = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Images',
                previous: JSON.stringify(prevImgObj),
                update: JSON.stringify(partner.images),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(imageChange);
        }
        ////End checks for changes to images////

        ////Checks for changes to icons////
        //check for changes in icon attributes
        let iconsChanged = false;

        if (this.partner.icons.svgURL !== partner.icons.svgURL) {
            iconsChanged = true;
        } else if (this.partner.icons.pngURL !== partner.icons.pngURL) {
            iconsChanged = true;
        } else if (this.partner.icons.base64URL !== partner.icons.base64URL) {
            iconsChanged = true;
        }

        if (iconsChanged) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Icons',
                previous: JSON.stringify(this.partner.icons),
                update: JSON.stringify(partner.icons),
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }
        ////End checks for changes to icons////

        //check if techDescription changed
        if (this.partner.techDescription !== partner.techDescription) {
            const change = {
                item: partner.item,
                name: partner.name,
                category: 'partner',
                field: 'Technical Description',
                previous: this.partner.techDescription,
                update: partner.techDescription,
                user: this.oktaService.username.value,
                date: date
            };

            changes.push(change);
        }

        if (changes.length > 0) {
            this.changeService.add(changes);
        }
    }

    savePartner() {
        //grab update date
        const date = new Date().toISOString();

        //turn full image element into image element
        let finalImages: ImageElement[] = [];
        this.images &&
            this.images.forEach(img => {
                const captionControl = 'caption' + img.id;
                const placementControl = 'placement' + img.id;

                if (img.uploadID) {
                    finalImages.push({
                        id: img.uploadID,
                        fileName: img.fileName,
                        visibleInSheet: img.placement !== 'Hidden',
                        type: img.type,
                        caption: this.imageForm.get(captionControl)!.value,
                        placement: this.imageForm.get(placementControl)!.value
                    });
                }
            });
        
        //turn icon URLs into object
        let iconURLS: IconElement = {
            'svgURL': this.partnerForm.controls['iconSVGControl'].value,
            'pngURL': this.partnerForm.controls['iconPNGControl'].value,
            'base64URL': this.partnerForm.controls['iconBase64Control'].value,
        }

        //turn previous names into object
        let previousNames: string[] = [];
        for (let i = 0; i < this.partnerForm.controls.previousNamesControl.length; i++) {
            previousNames.push(this.partnerForm.controls.previousNamesControl.controls[i].value);
        }

        //create the partner object to save
        const partner: PartnerElement = {
            partnerID: this.partnerID || -1,
            name: this.partnerForm.controls['nameControl'].value,
            item: this.partnerForm.controls['IDControl'].value!,
            statusID: this.partnerForm.controls['statusControl'].value!,
            published: this.partnerForm.controls['published'].value,
            revisionDate: date,
            tagline: this.partnerForm.controls['taglineControl'].value,
            problem: this.partnerForm.controls['problemControl'].value,
            approach: this.partnerForm.controls['approachControl'].value,
            features: this.partnerForm.controls['benefitsFeaturesControl'].value,
            images: finalImages,
            icons: iconURLS,
            techDescription: this.partnerForm.controls['techDescControl'].value,
            previousNames: previousNames,
            shortDescription: this.partnerForm.controls['shortDescription'].value,
            subtitle: this.partnerForm.controls['subtitle'].value,
            solutionArea: this.partnerForm.controls['solutionArea'].value
        };

        //if the partner is being edited, update it, log changes, and load changes
        if (this.partnerID) {
            this.partnerService
                .update(partner)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: () => {
                        this.logChanges(partner);
                        this.partnerService.refresh();
                        this.productService.refresh();
                        this.changeLog.loadChanges(this.itemID, "partner");

                        const options: HcToastOptions = {
                            header: 'Partner Updated',
                            body: partner.name + ' successfully updated.',
                            type: 'success',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);

                        //mark everything pristine, update partner variable, and update revision date
                        this.partnerForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.partner = partner;
                        this.revisionDate = date;
                    },
                    error: msg => {
                        const options: HcToastOptions = {
                            header: 'Update Failed',
                            body: 'Unable to update partner. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        } else {
            //if the partner is being added, add it, log an add change, update the item ID, and load changes
            this.partnerService
                .add(partner)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: result => {
                        this.changeService.add([
                            {
                                item: partner.item,
                                name: partner.name,
                                category: 'partner',
                                field: '--',
                                previous: '--',
                                update: 'partner added',
                                user: this.oktaService.username.value,
                                date: new Date().toISOString()
                            }
                        ]);
                        this.partnerService.refresh();
                        this.partnerID = result;
                        this.itemID = partner.item;
                        this.savedItem = partner.item;
                        this.changeLog.loadChanges(this.itemID, "partner");

                        const options: HcToastOptions = {
                            header: 'Partner Added',
                            body: partner.name + ' successfully added.',
                            type: 'success',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);

                        //mark everything pristine and update partner variable
                        this.partnerForm.markAsPristine();
                        this.imageForm.markAsPristine();
                        this.imagesEdited = false;
                        this.partner = partner;
                        this.revisionDate = date;
                    },
                    error: msg => {
                        const options: HcToastOptions = {
                            header: 'Add Failed',
                            body: 'Unable to add partner. Try again later.',
                            type: 'alert',
                            position: 'bottom-right'
                        };

                        this.toasterService.addToast(options);
                        console.log(msg);
                    }
                });
        }
    }

    openLightbox(img: FullImageElement) {
        const options: ModalOptions = {
            size: 'auto',
            closeIcon: true,
            data: {
                img
            }
        };

        this.modalService.open(LightboxComponent, options);
    }

    openPdf(): void {
        window.open('/viewer?id=' + this.itemID.toString(), '_blank');
    }

    generateLink() {
        const link = environment.root.url + '/viewer?id=' + this.itemID.toString();
        navigator.clipboard.writeText(link);

        const options: HcToastOptions = {
            header: 'Copied pdf URL for ' + this.partner.name,
            type: 'info',
            position: 'bottom-center'
        };
        this.toasterService.addToast(options);
    }

    //create an object from a string
    createObj(obj: any) {
        return JSON.parse(obj);
    }

    tabClick(selected: TabChangeEvent) {
        //ensure that newest changes are loaded if a user just updated
        selected.index === 1 && this.changeLog.loadChanges(this.itemID, "partner");
        this.selectedTab = selected.index;
    }

    canDeactivate(): boolean {
        return !this.partnerInputsDirty;
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
